import { getDeviceIdWithRetry } from "@/lib/seap"
import { useSearchParams } from "next/navigation"
import { useCallback } from "react"
import { UAParser } from "ua-parser-js"
import { BrowserName } from "ua-parser-js/enums"

// ua-parser-js's only "mobile variant" browser names, mapped back to their desktop equivalent
const MOBILE_BROWSER_NAME_TO_BASE: Record<string, string> = {
  [BrowserName.CHROME_MOBILE]: BrowserName.CHROME,
  [BrowserName.FIREFOX_MOBILE]: BrowserName.FIREFOX,
  [BrowserName.SAFARI_MOBILE]: BrowserName.SAFARI,
  [BrowserName.SOGOU_MOBILE]: BrowserName.SOGOU_EXPLORER,
}

export type DeviceInfo = {
  deviceName: string
  appName: string
  deviceId?: string
  pushId?: string
}

export const DEVICE_ID_COOKIE_NAME = "DEVICE_ID"

export function useDeviceInfo() {
  const searchParams = useSearchParams()
  const xtnType = searchParams.get("native_x") === "true" ? "native" : "web"

  return useCallback(async () => await getDeviceInfo(xtnType), [xtnType])
}

async function getDeviceInfo(xtnType: "web" | "native"): Promise<DeviceInfo> {
  if (xtnType === "native") {
    return await handleMobileDeviceInfo()
  } else {
    return await handleWebDeviceInfo()
  }
}

async function handleWebDeviceInfo(): Promise<DeviceInfo> {
  const deviceId = await getDeviceIdWithRetry()

  return {
    deviceName: getDeviceNameFromUA(),
    appName: getBrowserName(),
    deviceId,
    pushId: undefined,
  }
}

type MobileDeviceInfoResponse = {
  operation: "xtn_deviceid_response"
  deviceId: string
  appName?: string
  deviceName?: string
  pushId?: string
}

async function handleMobileDeviceInfo(): Promise<DeviceInfo> {
  const DEVICE_INFO_RESPONSE_TIMEOUT_MS = 30000
  const APP_NAME_DEFAULT = "Native App"

  return new Promise<DeviceInfo>((resolve) => {
    const messageHandler = (event: MessageEvent<MobileDeviceInfoResponse>) => {
      if (event.data.operation === "xtn_deviceid_response") {
        cleanup()

        const { deviceId, appName, deviceName, pushId } = event.data

        resolve({
          deviceId,
          appName: appName || APP_NAME_DEFAULT,
          deviceName: deviceName || getDeviceNameFromUA(),
          pushId,
        })
      }
    }

    const timeoutId = setTimeout(() => {
      cleanup()

      console.error(
        `Timed out after ${DEVICE_INFO_RESPONSE_TIMEOUT_MS}ms waiting for xtn_deviceid_response`,
      )

      resolve({
        deviceId: undefined,
        appName: APP_NAME_DEFAULT,
        deviceName: getDeviceNameFromUA(),
        pushId: undefined,
      })
    }, DEVICE_INFO_RESPONSE_TIMEOUT_MS)

    const cleanup = () => {
      window.removeEventListener("message", messageHandler)
      clearTimeout(timeoutId)
    }

    window.addEventListener("message", messageHandler) //NOSONAR, postMessage for apps
    window.postMessage({ operation: "xtn_deviceid_request" }, "*") //NOSONAR, postMessage for apps
  })
}

// Helper function to extract device name from User Agent using ua-parser-js
export function getDeviceNameFromUA(): string {
  const parser = new UAParser()

  const device = parser.getDevice()
  const os = parser.getOS()

  // If device has a model and vendor, use them
  if (device.model && device.vendor) {
    return `${device.vendor} ${device.model}`
  }

  // If device has a model only
  if (device.model) {
    return device.model
  }

  // Fallback to device type + OS
  if (device.type) {
    const typeName = device.type.charAt(0).toUpperCase() + device.type.slice(1)
    return os.name ? `${os.name} ${typeName}` : typeName
  }

  // Final fallback to OS name
  return os.name || "Unknown Device"
}

// Helper function to get browser name
export function getBrowserName(): string {
  const parser = new UAParser()
  const browserName = parser.getBrowser().name || "Web Browser"
  return MOBILE_BROWSER_NAME_TO_BASE[browserName] || browserName
}
